'use client';

import { useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogFooter,
  Input,
  Label,
  PageHeader,
  toast,
} from '@rr/ui';
import { request } from '@/lib/api/client';

/**
 * Minimum-viable classifiers admin (B8 v1).
 *
 * Two-pane layout per the spec: left rail of all handbook kinds, right
 * pane is an editable list. Inline add / edit / delete / active-toggle /
 * sort up/down. Drag-drop reorder is deliberately deferred — buttons
 * cover the same ground for now and avoid pulling in a DnD library.
 *
 * Source of truth for the kind list lives on the backend (`HandbookKind`
 * Prisma enum + Zod mirror in `src/modules/handbooks/types/index.ts`).
 * We mirror the slugs here so the rail renders without a /kinds endpoint
 * round-trip; the canonical list ships from the same string union.
 */

const HANDBOOK_KINDS = [
  'order_status',
  'cargo_status',
  'trip_status',
  'request_status',
  'document_status',
  'payment_type',
  'loading_method',
  'truck_body_type',
  'licence_category',
  'tag',
  'restriction',
  'vat_rate',
  'request_source',
  'request_purpose',
  'refusal_reason',
  'contact_position',
  'worker_post',
  'client_type',
  'carrier_type',
  'transport_type',
  'bank',
  'country',
  'region',
  'measurement_unit',
  'cargo_attribute',
  'additional_expense_category',
  'additional_expense_subcategory',
  'permission_role',
  'breaking_rule',
  'activity_area',
] as const;
type HandbookKind = (typeof HANDBOOK_KINDS)[number];

interface HandbookEntry {
  id: string;
  kind: HandbookKind;
  code: string;
  name: { en: string | null; ru: string | null; tk: string | null };
  nameResolved: string;
  parentId: string | null;
  meta: Record<string, unknown> | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  items: HandbookEntry[];
  pagination: { total: number };
}

function prettyKind(k: HandbookKind): string {
  return k
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function readColor(meta: Record<string, unknown> | null): string | null {
  if (!meta) return null;
  const c = meta.color;
  return typeof c === 'string' ? c : null;
}

interface EntryForm {
  code: string;
  en: string;
  ru: string;
  tk: string;
  color: string;
  isActive: boolean;
}

function emptyForm(): EntryForm {
  return { code: '', en: '', ru: '', tk: '', color: '', isActive: true };
}

function entryToForm(entry: HandbookEntry): EntryForm {
  return {
    code: entry.code,
    en: entry.name.en ?? '',
    ru: entry.name.ru ?? '',
    tk: entry.name.tk ?? '',
    color: readColor(entry.meta) ?? '',
    isActive: entry.isActive,
  };
}

function formToPayload(form: EntryForm): {
  code: string;
  name: { en?: string; ru?: string; tk?: string };
  meta: Record<string, unknown> | null;
  isActive: boolean;
} {
  const name: { en?: string; ru?: string; tk?: string } = {};
  if (form.en.trim()) name.en = form.en.trim();
  if (form.ru.trim()) name.ru = form.ru.trim();
  if (form.tk.trim()) name.tk = form.tk.trim();
  const meta = form.color.trim() ? { color: form.color.trim() } : null;
  return {
    code: form.code.trim(),
    name,
    meta,
    isActive: form.isActive,
  };
}

export default function SystemClassifiersPage() {
  const [selectedKind, setSelectedKind] =
    useState<HandbookKind>('order_status');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<HandbookEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EntryForm>(emptyForm());
  const queryClient = useQueryClient();

  const listKey = ['handbooks', selectedKind] as const;

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: listKey,
    queryFn: () =>
      request<ListResponse>(
        `/platform/handbooks/${selectedKind}?limit=200`,
      ),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => {
    const all = data?.items ?? [];
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      (entry) =>
        entry.code.toLowerCase().includes(needle) ||
        entry.nameResolved.toLowerCase().includes(needle),
    );
  }, [data, search]);

  const createMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof formToPayload>) =>
      request<HandbookEntry>(`/platform/handbooks/${selectedKind}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      toast('Classifier added', 'success');
      setCreating(false);
      setForm(emptyForm());
    },
    onError: (e: any) => toast(e?.message ?? 'Failed to add', 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ReturnType<typeof formToPayload>;
    }) =>
      request<HandbookEntry>(
        `/platform/handbooks/${selectedKind}/${id}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      toast('Classifier saved', 'success');
      setEditing(null);
      setForm(emptyForm());
    },
    onError: (e: any) => toast(e?.message ?? 'Failed to save', 'error'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      request<HandbookEntry>(
        `/platform/handbooks/${selectedKind}/${id}`,
        { method: 'PATCH', body: JSON.stringify({ isActive }) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey }),
    onError: (e: any) => toast(e?.message ?? 'Failed to toggle', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/platform/handbooks/${selectedKind}/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      toast('Classifier deleted', 'success');
    },
    onError: (e: any) => toast(e?.message ?? 'Failed to delete', 'error'),
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      request<ListResponse>(
        `/platform/handbooks/${selectedKind}/reorder`,
        { method: 'POST', body: JSON.stringify({ orderedIds }) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey }),
    onError: (e: any) => toast(e?.message ?? 'Failed to reorder', 'error'),
  });

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const orderedIds = items.map((e) => e.id);
    [orderedIds[index], orderedIds[target]] = [
      orderedIds[target]!,
      orderedIds[index]!,
    ];
    reorderMutation.mutate(orderedIds);
  };

  const openCreate = () => {
    setForm(emptyForm());
    setCreating(true);
  };

  const openEdit = (entry: HandbookEntry) => {
    setForm(entryToForm(entry));
    setEditing(entry);
  };

  const closeDialog = () => {
    setEditing(null);
    setCreating(false);
    setForm(emptyForm());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast('Code is required', 'error');
      return;
    }
    if (!form.en.trim() && !form.ru.trim() && !form.tk.trim()) {
      toast('At least one locale name is required', 'error');
      return;
    }
    const payload = formToPayload(form);
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Classifiers"
        subtitle="Edit status labels, types, tags, and other lookup values without a code change."
      />
      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-200px)] min-h-[500px]">
        {/* Left rail — kind list */}
        <aside className="w-full lg:w-64 shrink-0 overflow-y-auto rounded-xl border border-border bg-card">
          <ul className="p-1">
            {HANDBOOK_KINDS.map((kind) => (
              <li key={kind}>
                <button
                  type="button"
                  onClick={() => setSelectedKind(kind)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedKind === kind
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  {prettyKind(kind)}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Right pane — list editor */}
        <section className="flex-1 min-w-0 overflow-hidden rounded-xl border border-border bg-card flex flex-col">
          <header className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <h2 className="text-base font-semibold flex-1">{prettyKind(selectedKind)}</h2>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code or name…"
              className="w-56"
            />
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </header>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-sm font-medium text-foreground">No entries</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {search.trim()
                    ? 'No matches for your search.'
                    : 'Click "Add" to create the first entry for this kind.'}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((entry, idx) => {
                  const color = readColor(entry.meta);
                  return (
                    <li
                      key={entry.id}
                      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 ${
                        !entry.isActive ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          type="button"
                          aria-label="Move up"
                          disabled={idx === 0 || reorderMutation.isPending}
                          onClick={() => handleMove(idx, -1)}
                          className="h-5 w-5 grid place-items-center rounded text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          aria-label="Move down"
                          disabled={
                            idx === items.length - 1 || reorderMutation.isPending
                          }
                          onClick={() => handleMove(idx, 1)}
                          className="h-5 w-5 grid place-items-center rounded text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </div>
                      {color && (
                        <span
                          aria-hidden
                          className="h-4 w-4 rounded-full border border-border shrink-0"
                          style={{ backgroundColor: color }}
                        />
                      )}
                      <code className="text-[11px] font-mono text-muted-foreground shrink-0 min-w-[120px]">
                        {entry.code}
                      </code>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">
                          {entry.nameResolved}
                        </p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {[entry.name.en, entry.name.ru, entry.name.tk]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          toggleActiveMutation.mutate({
                            id: entry.id,
                            isActive: !entry.isActive,
                          })
                        }
                        className={`px-2 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                          entry.isActive
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300'
                            : 'bg-muted text-muted-foreground hover:bg-muted-foreground/20'
                        }`}
                        title={entry.isActive ? 'Disable' : 'Enable'}
                      >
                        {entry.isActive ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(entry)}
                        className="h-7 w-7 grid place-items-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete "${entry.nameResolved}"?`)) {
                            deleteMutation.mutate(entry.id);
                          }
                        }}
                        className="h-7 w-7 grid place-items-center rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Add / Edit dialog */}
      <Dialog
        open={creating || editing !== null}
        onClose={closeDialog}
        title={editing ? `Edit ${prettyKind(selectedKind)}` : `Add ${prettyKind(selectedKind)}`}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label>Code</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. IN_PROGRESS"
              disabled={!!editing}
            />
            {editing && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Code can&apos;t change once created — it&apos;s the join key for existing records.
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Name (EN)</Label>
              <Input
                value={form.en}
                onChange={(e) => setForm({ ...form, en: e.target.value })}
              />
            </div>
            <div>
              <Label>Name (RU)</Label>
              <Input
                value={form.ru}
                onChange={(e) => setForm({ ...form, ru: e.target.value })}
              />
            </div>
            <div>
              <Label>Name (TK)</Label>
              <Input
                value={form.tk}
                onChange={(e) => setForm({ ...form, tk: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Color (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#3b82f6"
              />
              {form.color && (
                <span
                  aria-hidden
                  className="h-7 w-7 rounded-md border border-border shrink-0"
                  style={{ backgroundColor: form.color }}
                />
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="h-4 w-4"
            />
            <span>Active</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDialog}>
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </div>
  );
}
